import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@genius-tracker/i18n/react";
import { API_CONTEXT } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import type { RealtimeClient, RealtimeKind, RealtimeListener, RealtimeStatus } from "./client.js";
import type {
  createInvalidationCoalescer,
  CoalescedInvalidation,
} from "./invalidationCoalescer.js";

/**
 * GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 (Wave E) — the coalescer
 * family for `notifications.hint`. Exported so the Sprint 4 tests
 * exercise the EXACT production config (window ≥ 15 s is a binding
 * Phase 0 §6 contract; the source guard pins it too).
 *
 * Prefix invalidation of `["me","notifications"]` reaches the
 * unread-count badge (always mounted → refetches) and the Center
 * list (refetches only while the page is open — TanStack default
 * `refetchType: "active"`).
 */
export const NOTIFICATIONS_HINT_INVALIDATION = {
  queryKey: ["me", "notifications"],
  exact: false,
  minIntervalMs: 15_000,
} as const satisfies CoalescedInvalidation;

/**
 * React-side host for the realtime singleton.
 *
 *  - Owns exactly one `RealtimeClient` for the tab.
 *  - Starts / stops it based on session presence (no session → no WS).
 *  - Invalidates TanStack Query keys on the two Sprint-8 topics so
 *    /map and /dashboard re-fetch immediately on NOTIFY.
 *  - Exposes status to any consumer (small "live" dot in page headers,
 *    first-failure banner in AppShell).
 *
 * Sprint 8 Wave D UX decisions (from Phase 0):
 *  - First WS failure shows a non-intrusive banner exactly once per
 *    session. Subsequent failures stay silent; polling resumes at
 *    the pre-Sprint-8 cadence automatically.
 *  - Status dot distinguishes live (WS open), reconnecting (backoff),
 *    polling (WS down, HTTP fallback active), paused (tab hidden).
 *
 * E-BUNDLE-18 — the transport (`client.ts` plus the coalescer) is
 * pulled with `import()` instead of hanging in the entry chunk. The
 * provider itself stays static: it holds the context that every page
 * hook reads, so gating the provider would remount the whole app
 * subtree the moment a real provider replaced a placeholder.
 * Deferring only the transport costs nothing — the socket starts
 * post-mount anyway, and until the module lands the app behaves
 * exactly as it does while the WebSocket is still connecting: status
 * `idle`, every query on its polling cadence.
 */

/** The lazily loaded transport, kept together so one state flip arms every effect. */
interface RealtimeTransport {
  readonly client: RealtimeClient;
  readonly createCoalescer: typeof createInvalidationCoalescer;
}

/**
 * A `subscribe()` call that arrived before the transport finished
 * loading. It is replayed against the real client on arrival, so no
 * consumer has to know that the module is deferred.
 */
interface PendingSubscription {
  readonly kind: RealtimeKind;
  readonly listener: RealtimeListener;
  off?: () => void;
  cancelled?: boolean;
}

interface RealtimeContextValue {
  readonly status: RealtimeStatus;
  readonly hasFailedOnce: boolean;
  readonly subscribe: (kind: RealtimeKind, listener: RealtimeListener) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function useRealtimeContext(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtimeContext must be used inside <RealtimeProvider>");
  return ctx;
}

/** The two lazily loaded transport modules (E-BUNDLE-18). */
const loadTransportModules = () =>
  Promise.all([import("./client.js"), import("./invalidationCoalescer.js")]);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const session = useSession();
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const [hasFailedOnce, setHasFailedOnce] = useState(false);
  const [transport, setTransport] = useState<RealtimeTransport | null>(null);
  const clientRef = useRef<RealtimeClient | null>(null);
  const pendingRef = useRef<PendingSubscription[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let mods: Awaited<ReturnType<typeof loadTransportModules>>;
      try {
        mods = await loadTransportModules();
      } catch (err) {
        // The transport chunk did not load (offline, stale deploy). Same
        // outcome as a first WebSocket failure: the existing failure banner
        // shows once and every query stays on its polling cadence.
        console.error("realtime transport failed to load", err);
        if (!cancelled) setHasFailedOnce(true);
        return;
      }
      const [{ RealtimeClient }, { createInvalidationCoalescer }] = mods;
      if (cancelled) return;
      const client = new RealtimeClient({ apiBase: API_CONTEXT.baseUrl });
      clientRef.current = client;
      // Replay whatever subscribed while the module was in flight.
      const pending = pendingRef.current;
      pendingRef.current = [];
      for (const entry of pending) {
        if (!entry.cancelled) entry.off = client.subscribe(entry.kind, entry.listener);
      }
      setTransport({ client, createCoalescer: createInvalidationCoalescer });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const client = transport?.client;
    if (!client) return;
    const offStatus = client.onStatus((s) => {
      setStatus(s);
    });
    const offFailed = client.onFailedOnce(() => {
      setHasFailedOnce(true);
    });
    return () => {
      offStatus();
      offFailed();
    };
  }, [transport]);

  useEffect(() => {
    const client = transport?.client;
    if (!client) return;
    if (!session) {
      client.stop();
      return;
    }
    client.start();
    return () => {
      // Keep the socket alive across re-renders; only stop on
      // unmount (handled by the outer effect when session flips).
    };
  }, [transport, session]);

  useEffect(() => {
    if (!transport) return;
    const { client, createCoalescer } = transport;
    // GT-…-FIX-09 (Wave 1) — `fleet.positions` NOTIFYs arrive at ingest
    // rate (~3.5/s on Urbagec). Invalidating heavy queries per packet
    // refetched the roster + positions continuously and saturated the
    // shared `ui_read_heavy` 300/min budget (the proven 429 root cause).
    // Coalesce: at most ONE invalidation per family per window.
    //  - positions latest/history (2s): drives marker headings
    //    (Sprint 28 Wave C) and route trails (Wave D) — live enough for
    //    dispatching at a bounded refetch rate.
    //  - vehicle roster (15s): markers read `lastLocation` from the
    //    roster DTO so it still refreshes, but never per-packet. The
    //    60s safety poll remains the backstop. Roster invalidation also
    //    re-fans the paginated list, so it is the most expensive family.
    const coalescer = createCoalescer(
      (family) => qc.invalidateQueries({ queryKey: [...family.queryKey], exact: family.exact }),
      [
        { queryKey: ["telemetry", "positions", "latest"], exact: false, minIntervalMs: 2_000 },
        { queryKey: ["telemetry", "positions", "history"], exact: false, minIntervalMs: 2_000 },
        { queryKey: ["fleet", "vehicles"], exact: false, minIntervalMs: 15_000 },
      ],
    );
    const offPositions = client.subscribe("fleet.positions", () => {
      coalescer.notify();
    });
    const offSummary = client.subscribe("fleet.summary", () => {
      // Invalidate ONLY the scalar summary card so `/dashboard`
      // re-fetches. `exact: true` is load-bearing — the 24 h
      // trends hook uses `["fleet", "summary", "history", N]`, and
      // a prefix match would pull trends off its intentional 60 s
      // polling cadence on every vehicle mutation. Wave D keeps
      // trends explicitly polling-only (Phase 0 / ROADMAP §9).
      qc.invalidateQueries({ queryKey: ["fleet", "summary"], exact: true });
    });
    // Sprint 9 Wave D — geofence events + trips. Server publishes
    // a hint-only payload per ingest batch; the client refetches
    // the list query on the affected pages. Prefix-match is safe
    // here because `trips` list queries are keyed by filters and
    // we want every open filtered view to refresh.
    const offGeofenceEvents = client.subscribe("fleet.geofence-events", () => {
      qc.invalidateQueries({ queryKey: ["geofence-events"], exact: false });
    });
    const offTrips = client.subscribe("fleet.trips", () => {
      qc.invalidateQueries({ queryKey: ["trips"], exact: false });
    });
    // GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 (Wave E) — the hint is a
    // tenant-level, payload-free ping (server pins `{}`; DL-086
    // D-S3-4). The listener deliberately takes NO event parameter:
    // the payload carries nothing and must never be read
    // (source-guarded). A SEPARATE coalescer instance is load-bearing
    // — the positions coalescer above notifies ALL of its families
    // per `fleet.positions` packet, so sharing it would invalidate
    // the notification queries at ingest rate instead of at hint
    // rate. Window: one invalidation per ≥15 s no matter how many
    // hints arrive; the 60 s unread-count poll remains the backstop.
    const notificationsCoalescer = createCoalescer(
      (family) => qc.invalidateQueries({ queryKey: [...family.queryKey], exact: family.exact }),
      [NOTIFICATIONS_HINT_INVALIDATION],
    );
    const offNotificationsHint = client.subscribe("notifications.hint", () => {
      notificationsCoalescer.notify();
    });
    return () => {
      coalescer.dispose();
      notificationsCoalescer.dispose();
      offPositions();
      offSummary();
      offGeofenceEvents();
      offTrips();
      offNotificationsHint();
    };
  }, [transport, qc]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      status,
      hasFailedOnce,
      subscribe: (kind, listener) => {
        const client = clientRef.current;
        if (client) return client.subscribe(kind, listener);
        // Transport still in flight — remember the call and hand back an
        // unsubscribe that works before and after the module lands.
        const entry: PendingSubscription = { kind, listener };
        pendingRef.current.push(entry);
        return () => {
          entry.cancelled = true;
          entry.off?.();
        };
      },
    }),
    [status, hasFailedOnce],
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
      <RealtimeFailureBannerGate />
    </RealtimeContext.Provider>
  );
}

/**
 * E-BUNDLE-24 — Ladeschranke vor dem Fehlerbanner.
 *
 * Das Banner erscheint nur, wenn der WebSocket-Transport ueberhaupt
 * nicht aufgebaut werden kann. Die Vorbedingung dafuer ist billig
 * (`hasFailedOnce` aus dem Kontext) und bleibt hier, ebenso der
 * Auto-Dismiss-Timer. Das Markup selbst kommt erst im Fehlerfall per
 * import() nach und liegt damit nicht mehr im Entry-Chunk.
 */
const RealtimeFailureBanner = lazy(async () => {
  const mod = await import("./RealtimeFailureBanner.js");
  return { default: mod.RealtimeFailureBanner };
});

function RealtimeFailureBannerGate() {
  const { hasFailedOnce, status } = useRealtimeContext();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!hasFailedOnce) return;
    const tid = setTimeout(() => setDismissed(true), 10_000);
    return () => clearTimeout(tid);
  }, [hasFailedOnce]);

  if (!hasFailedOnce || dismissed || status === "live") return null;

  return (
    <Suspense fallback={null}>
      <RealtimeFailureBanner
        onDismiss={() => {
          setDismissed(true);
        }}
      />
    </Suspense>
  );
}

/**
 * Hook for page headers to render a small status chip next to the
 * title. Consolidates page-visibility + realtime status into a
 * single display label.
 */
export function useRealtimeStatusLabel(visible: boolean): {
  tone: "live" | "paused" | "polling" | "reconnecting";
  label: string;
} {
  const { t } = useLocale();
  const { status } = useRealtimeContext();
  if (!visible) return { tone: "paused", label: t("map.live.paused") };
  if (status === "live") return { tone: "live", label: t("realtime.status.live") };
  if (status === "reconnecting" || status === "connecting")
    return { tone: "reconnecting", label: t("realtime.status.reconnecting") };
  if (status === "polling") return { tone: "polling", label: t("realtime.status.polling") };
  return { tone: "polling", label: t("realtime.status.polling") };
}
